import React, { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { useSnackbar } from '../config/useSnackbar';
import { useUser } from '../auth/useUser';
import { useOrgInfo } from '../auth/use-org-info/use-org-info';
import {
  SetupProgress,
  useGetSetupProgressFetch,
  useCompleteStageFetch,
  useMarkDialogViewedFetch,
  transformSetupProgressResponse,
} from './tenant-setup-actions';

export const SETUP_STAGES = [
  { key: 'intro', label: 'Quick Questions', order: 1 },
  { key: 'programs', label: 'Programs', order: 2 },
  { key: 'tasks', label: 'Tasks & Onboarding', order: 3 },
  { key: 'activities', label: 'Activities & Reporting', order: 4 },
  { key: 'review', label: 'Review', order: 5 },
  { key: 'invite', label: 'Invite First Volunteer', order: 6 },
  { key: 'downloadApp', label: 'Download the App', order: 7 },
  { key: 'bookCall', label: 'Book a Review Call', order: 8 },
] as const;

export type SetupStageKey = typeof SETUP_STAGES[number]['key'];

export interface TenantSetupContextInterface {
  isLoading: boolean;
  setupDialogViewed: boolean;
  setupProgress: SetupProgress | null;
  currentStageKey: SetupStageKey | null;
  completedStages: string[];
  progressPercentage: number;
  questionnaireAnswers: Record<string, unknown> | null;
  shouldShowSetupDialog: boolean;
  completeStage: (stageKey: SetupStageKey, data?: Record<string, unknown>) => Promise<boolean>;
  saveQuestionnaireAnswers: (answers: Record<string, unknown>) => Promise<boolean>;
  markDialogViewed: () => Promise<void>;
  refreshProgress: () => void;
  openSetupDialog: () => void;
  closeSetupDialog: () => void;
  isSetupDialogOpen: boolean;
}

export const TenantSetupContext = createContext<TenantSetupContextInterface | undefined>(undefined);

export const TenantSetupProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [setupDialogViewed, setSetupDialogViewed] = useState(true);
  const [setupProgress, setSetupProgress] = useState<SetupProgress | null>(null);
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);

  const { setSnackbar } = useSnackbar();
  const {
    user: { userIdentityService },
  } = useUser();
  const orgInfo = useOrgInfo();

  const getProgressFetch = useGetSetupProgressFetch();
  const completeStageFetch = useCompleteStageFetch();
  const markViewedFetch = useMarkDialogViewedFetch();

  const isAdmin = userIdentityService.isVmAdmin;
  const isTrial = orgInfo?.inTrial ?? false;

  const fetchProgress = useCallback(async () => {
    if (!isAdmin || !isTrial) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await getProgressFetch.run({});
      if (response.ok && response.data?.data) {
        const rawData = response.data.data;
        const transformed = transformSetupProgressResponse(rawData);
        setSetupDialogViewed(transformed.setupDialogViewed);
        setSetupProgress(transformed.setupProgress);

        if (!transformed.setupDialogViewed) {
          setIsSetupDialogOpen(true);
        }
      }
    } catch {
      // Failed to fetch setup progress
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, isTrial]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const completeStage = async (stageKey: SetupStageKey, data?: Record<string, unknown>): Promise<boolean> => {
    try {
      const response = await completeStageFetch.run({ stageKey, ...data });
      if (response.ok && response.data?.data) {
        const { progressPercentage: newProgress, currentStage } = response.data.data;
        // Update local state with new completed stage
        setSetupProgress((prev) => {
          if (!prev) return prev;
          const updatedStages = prev.stagesCompleted.includes(stageKey)
            ? prev.stagesCompleted
            : [...prev.stagesCompleted, stageKey];
          const updatedStageData = { ...prev.stageData };
          if (data) {
            updatedStageData[stageKey] = data;
          }
          return {
            ...prev,
            stagesCompleted: updatedStages,
            stageData: updatedStageData,
            progressPercentage: newProgress,
            currentStage,
          };
        });
        setSnackbar({
          open: true,
          message: 'Stage completed',
          variant: 'success',
        });
        return true;
      }
      setSnackbar({
        open: true,
        message: 'Failed to save progress',
        variant: 'error',
      });
      return false;
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to save progress',
        variant: 'error',
      });
      return false;
    }
  };

  const saveQuestionnaireAnswers = async (answers: Record<string, unknown>): Promise<boolean> => {
    const success = await completeStage('intro', { questionnaireAnswers: answers });
    if (success) {
      setSetupProgress((prev) => (prev ? { ...prev, questionnaireAnswers: answers } : prev));
    }
    return success;
  };

  const markDialogViewed = async (): Promise<void> => {
    if (setupDialogViewed) return;

    try {
      const response = await markViewedFetch.run({});
      if (response.ok) {
        setSetupDialogViewed(true);
      }
    } catch {
      console.error('Failed to mark dialog as viewed');
    }
  };

  const openSetupDialog = () => setIsSetupDialogOpen(true);
  const closeSetupDialog = () => {
    setIsSetupDialogOpen(false);
    markDialogViewed();
  };

  const completedStages = setupProgress?.stagesCompleted ?? [];
  const currentStageKey = (setupProgress?.currentStage as SetupStageKey) ?? null;
  const progressPercentage = setupProgress?.progressPercentage ?? 0;
  const questionnaireAnswers = setupProgress?.questionnaireAnswers ?? null;
  const shouldShowSetupDialog = isAdmin && isTrial && !setupDialogViewed;

  return (
    <TenantSetupContext.Provider
      value={{
        isLoading,
        setupDialogViewed,
        setupProgress,
        currentStageKey,
        completedStages,
        progressPercentage,
        questionnaireAnswers,
        shouldShowSetupDialog,
        completeStage,
        saveQuestionnaireAnswers,
        markDialogViewed,
        refreshProgress: fetchProgress,
        openSetupDialog,
        closeSetupDialog,
        isSetupDialogOpen,
      }}
    >
      {children}
    </TenantSetupContext.Provider>
  );
};
